import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import {
  ArrowDownCircle,
  Check,
  GraduationCap,
  Loader2,
  PauseCircle,
  PlayCircle,
  SlidersHorizontal,
  Star,
  StarOff,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";

import { APPLY_REPORT_ACTION, UNDO_REPORT_ACTION } from "@/graphql/mutations/reports";
import { Button } from "@/components/ui/button";

/**
 * The buttons under a guardian's report.
 *
 * A report already ends in „Ką daryti", and that section is advice for the kitchen
 * table — the right thing to say, and the wrong place to stop. Everything the app
 * itself could change about the same finding (serve this topic easier, set it
 * aside, aim sessions at it, mark it as already known) lived on a different screen,
 * in a different vocabulary, behind a grown-up remembering the finding long enough
 * to go and act on it. These are the same decisions, offered where the problem was
 * just described.
 *
 * Nothing here is composed in the browser. `label`, `evidence` and `effect` are
 * derived on the server from the same facts the prose was written from and frozen
 * on the row — the report's own rule, one table over: a sentence next to a button
 * that changes a child's record must say what the numbers said at the time, not
 * what a wording change made it say later.
 */

export type ReportAction = {
  id: string;
  kind: string;
  skillKey?: string | null;
  skillName?: string | null;
  label: string;
  evidence?: string | null;
  effect?: string | null;
  rationale?: string | null;
  applied: boolean;
  appliedAt?: string | null;
  appliedByRole?: string | null;
};

/**
 * One icon per kind, and the pairs deliberately mirror each other: easing and
 * restoring, pausing and resuming. A grown-up who has applied one is looking at
 * its opposite in the next report, and the shape is what tells them so.
 */
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  ease_level: ArrowDownCircle,
  restore_level: SlidersHorizontal,
  pause_skill: PauseCircle,
  resume_skill: PlayCircle,
  mark_known: GraduationCap,
  prioritize: Star,
  unprioritize: StarOff,
};

function ActionRow({ action }: { action: ReportAction }) {
  const [busy, setBusy] = useState(false);
  const [apply] = useMutation(APPLY_REPORT_ACTION);
  const [undo] = useMutation(UNDO_REPORT_ACTION);

  const Icon = ICONS[action.kind] ?? SlidersHorizontal;

  async function run(undoing: boolean) {
    setBusy(true);
    try {
      const result = undoing
        ? await undo({ variables: { id: action.id } })
        : await apply({ variables: { id: action.id } });
      const payload = (result.data as any)?.[undoing ? "undoReportAction" : "applyReportAction"];
      if (payload?.errors?.length) {
        toast.error(payload.errors[0]);
        return;
      }
      toast.success(undoing ? "Grąžinta, kaip buvo" : "Pritaikyta — galios nuo kitos sesijos");
    } catch {
      toast.error(undoing ? "Nepavyko atšaukti" : "Nepavyko pritaikyti");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-background/60 p-3 sm:flex-row sm:items-start">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />

      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium">
          {action.label}
          {action.skillName && (
            <span className="font-normal text-muted-foreground"> · {action.skillName}</span>
          )}
        </p>
        {action.rationale && <p className="text-sm text-muted-foreground">{action.rationale}</p>}
        <p className="text-xs text-muted-foreground/80">
          {action.evidence && (
            <>
              <span className="font-medium">Iš kur matosi: </span>
              {action.evidence}
            </>
          )}
          {action.evidence && action.effect ? " · " : ""}
          {action.effect}
        </p>
      </div>

      {action.applied ? (
        <div className="flex shrink-0 items-center gap-2">
          <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <Check className="h-4 w-4" />
            Pritaikyta
          </span>
          {/*
            Every one of these is offered on the strength of a couple of weeks,
            which is a good reason to try one and a bad reason to be stuck with it.
            The way back is what makes pressing the first one cheap.
          */}
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(true)}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
            <span className="ml-1">Atšaukti</span>
          </Button>
        </div>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          disabled={busy}
          onClick={() => run(false)}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Pritaikyti
        </Button>
      )}
    </div>
  );
}

export function ReportActionsPanel({ actions }: { actions?: ReportAction[] | null }) {
  if (!actions || actions.length === 0) return null;

  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <SlidersHorizontal className="h-4 w-4 text-primary" />
        Ką pakeisti programoje
      </div>
      {/*
        Named apart from „Ką daryti" on purpose. That section is what to do with
        the child; this is what the app will do differently — which topics come up,
        at what level, and what the sessions aim at. Folding them together would
        make one of them read as the other's fine print.
      */}
      <p className="mb-3 text-xs text-muted-foreground">
        Šie pakeitimai veikia būsimas sesijas. Vaikas jų nemato, o kiekvieną galima
        atšaukti.
      </p>
      <div className="space-y-2">
        {actions.map((action) => (
          <ActionRow key={action.id} action={action} />
        ))}
      </div>
    </div>
  );
}
